import React, { useEffect } from 'react'
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  TableBody,
  Typography,
  useMediaQuery,
  useTheme,
  List as MuiList,
  ListItemButton,
  Collapse,
  styled,
  Grid,
  Link,
} from '@mui/material'
import {
  getSalesOrders,
  selectSalesOrderByAgreementId,
  selectSalesOrderRequestStatusByAgreementId,
} from './storage'
import { TestIds } from '../../testIds'
import { useSelector } from 'react-redux'
import { Loader } from '../../components/Loader'
import { useAppDispatch } from '../../store/rootStore'
import dayjs from 'dayjs'
import { formatUnitDisplay } from './SalesLine'
import { useNavigate } from 'react-router-dom'

const SalesOrdersContainer = styled('div')({})

const SalesOrdersView = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '8px',
})

const List = styled(MuiList)({
  width: '100%',
  padding: 0,
  gap: 0,
})

const HeaderView = styled(ListItemButton)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.colors.primary100,
  margin: 0,
  padding: '4px 0',
  '& .MuiGrid-item': {
    padding: '4px 16px',
    '& .material-symbols-outlined': {
      height: '21px',
      color: theme.colors.primary500,
    },
    '& .MuiTypography-root': {
      ...theme.typography.body2,
    },
    '&:nth-child(1)': {
      '& .MuiTypography-root': {
        fontWeight: 700,
      },
    },
  },
  '&:hover': {
    backgroundColor: theme.colors.primary200,
    color: theme.colors.primary900,
  },
}))

const BodyView = styled(Collapse)(({ theme }) => ({
  border: 'solid',
  borderWidth: '0 1px 1px 1px ',
  borderColor: theme.colors.primary100,
  '& .MuiGrid-item': {
    padding: '4px 16px',
    '&.title': {
      '& .MuiTypography-root': {
        ...theme.typography.body2,
        color: theme.colors.grey700,
      },
    },
    '& .material-symbols-outlined': {
      fontSize: '16px',
      color: theme.colors.primary500,
      marginLeft: '4px',
    },
    '& .MuiTypography-root': {
      ...theme.typography.body2,
    },
  },
}))

const WrapperFlexend = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
})

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&.MuiTableCell-body': {
    padding: '16px 16px 16px 0',
  },
  '&.MuiTableCell-alignRight': {
    padding: '16px 0px 16px 16px',
  },
  '& .material-symbols-outlined': {
    color: theme.colors.primary500,
  },
}))

interface SalesOrdersProps {
  agreementId: string
}

const SalesOrders = ({ agreementId }: SalesOrdersProps) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = React.useState({ id: '', status: false })
  const data = useSelector(selectSalesOrderByAgreementId(agreementId))
  const status = useSelector(selectSalesOrderRequestStatusByAgreementId(agreementId))
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(getSalesOrders(agreementId))
  }, [])

  const orderDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    orderId: string,
  ) => {
    e?.preventDefault()
    navigate(`${path}${orderId}`, {
      state: {
        orderId,
      },
    })
  }

  const handleClick = (id: string) => {
    if (open.id === id) {
      setOpen({ id, status: !open.status })
    } else {
      setOpen({ id, status: true })
    }
  }

  const renderMobile = () => {
    const expandIcon = (id: string) => (
      <WrapperFlexend>
        <span className='material-symbols-outlined'>
          {open.id === id && open.status ? 'expand_less' : 'expand_more'}
        </span>
      </WrapperFlexend>
    )

    return (
      <SalesOrdersView data-testid={TestIds.feat.agreement.salesOrder.table}>
        {data?.map((order) => (
          <List
            key={order.id}
            data-testid={TestIds.feat.agreement.salesOrder.tableItem}
            data-id={order.id}
          >
            <HeaderView onClick={() => handleClick(order.id)}>
              <Grid container>
                <Grid item xs={3}>
                  <Typography>Date</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography>{dayjs(order.date).format('DD/MM/YYYY')}</Typography>
                </Grid>
                <Grid item xs={2}>
                  {expandIcon(order.id)}
                </Grid>
              </Grid>
              <Grid container>
                <Grid item xs={3}>
                  <Typography>Status</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>{order.status}</Typography>
                </Grid>
              </Grid>
            </HeaderView>
            <BodyView in={open.id === order.id && open.status} timeout='auto' unmountOnExit>
              <Grid container>
                <Grid item className='title' xs={3}>
                  <Typography>ID</Typography>
                </Grid>
                <Grid item xs={9}>
                  <Typography>{order.id}</Typography>
                </Grid>
                <Grid item className='title' xs={3}>
                  <Typography>Product</Typography>
                </Grid>
                <Grid item xs={9}>
                  {order.products.map(({ productDescription }) => (
                    <Typography key={productDescription}>{productDescription}</Typography>
                  ))}
                </Grid>
                <Grid item className='title' xs={3}>
                  <Typography>Amount</Typography>
                </Grid>
                <Grid item xs={9}>
                  {order.products.map(({ amount, unit }) => (
                    <Typography key={amount}>{`${amount}${formatUnitDisplay(unit)}`}</Typography>
                  ))}
                </Grid>
              </Grid>
            </BodyView>
          </List>
        ))}
      </SalesOrdersView>
    )
  }

  const render = () => (
    <TableContainer data-testid={TestIds.feat.agreement.salesOrder.table}>
      <Table aria-label='Sales orders table'>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((order) => (
            <TableRow
              key={order.id}
              data-testid={TestIds.feat.agreement.salesOrder.tableItem}
              data-id={order.id}
            >
              <TableCell component='th' scope='row'>
                {dayjs(order.date).format('DD/MM/YYYY')}
              </TableCell>
              <TableCell>{order.status}</TableCell>
              <TableCell>{order.id}</TableCell>
              <TableCell>
                {order.products.map(({ productDescription }) => (
                  <React.Fragment key={productDescription}>
                    <span key={productDescription}>{productDescription}</span>
                    <br />
                  </React.Fragment>
                ))}
              </TableCell>
              <TableCell>
                {order.products.map(({ amount, unit }) => (
                  <React.Fragment key={amount}>
                    <span>{`${amount}${formatUnitDisplay(unit)}`}</span>
                    <br />
                  </React.Fragment>
                ))}
              </TableCell>
              <TableCell>
                <Link variant='body2' onClick={(e) => orderDetailsNavigate(e, '/order/', order.id)}>
                  View order
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  const renderBody = React.useMemo(() => {
    if (!data && status === 'pending') {
      return <Loader title='Loading sales orders' />
    }
    if ((!data || data.length === 0) && status === 'fulfilled') {
      return (
        <Typography variant='body2'>
          You do not currently have any Sales Orders for this Sales Agreement.
        </Typography>
      )
    }
    if (isSmall) {
      return renderMobile()
    } else {
      return render()
    }
  }, [data, status, isSmall, open])

  return (
    <SalesOrdersContainer>
      <Typography variant='h4' sx={{ mb: 2 }}>
        Sales Orders
      </Typography>
      {renderBody}
    </SalesOrdersContainer>
  )
}

export default SalesOrders
